import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiAlertCircle, FiAlertTriangle, FiInfo, FiCheckCircle, FiChevronDown, FiChevronRight, FiX, FiMove, FiMinus } from 'react-icons/fi';
import { ValidationIssue } from '../../utils/resourceValidator';

interface ValidationPanelProps {
  issues: ValidationIssue[];
  onClose: () => void;
  onSelectNode?: (nodeId: string) => void;
}

const ValidationPanel: React.FC<ValidationPanelProps> = ({ issues, onClose, onSelectNode }) => {
  const { t } = useTranslation('canvas');
  const [isOpen, setIsOpen] = useState(true);
  const [expandedSeverity, setExpandedSeverity] = useState<string | null>('error');
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState<{ x: number; y: number }>(() => {
    // Load saved position from localStorage
    const saved = localStorage.getItem('validationPanel.position');
    if (saved) {
      return JSON.parse(saved);
    }
    return { x: 16, y: window.innerHeight - 600 };
  });
  const dragRef = useRef<{ startX: number; startY: number; startPosX: number; startPosY: number } | null>(null);

  // Save position to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('validationPanel.position', JSON.stringify(position));
  }, [position]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.drag-handle')) {
      setIsDragging(true);
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startPosX: position.x,
        startPosY: position.y,
      };
      e.preventDefault();
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && dragRef.current) {
        const deltaX = e.clientX - dragRef.current.startX;
        const deltaY = e.clientY - dragRef.current.startY;
        setPosition({
          x: Math.max(0, Math.min(window.innerWidth - 380, dragRef.current.startPosX + deltaX)),
          y: Math.max(0, Math.min(window.innerHeight - 100, dragRef.current.startPosY + deltaY)),
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      dragRef.current = null;
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const errors = issues.filter(i => i.severity === 'error');
  const warnings = issues.filter(i => i.severity === 'warning');
  const infos = issues.filter(i => i.severity === 'info');

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'error':
        return <FiAlertCircle size={16} style={{ color: '#EF4444' }} />;
      case 'warning':
        return <FiAlertTriangle size={16} style={{ color: '#F59E0B' }} />;
      case 'info':
        return <FiInfo size={16} style={{ color: '#3B82F6' }} />;
      default:
        return null;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'error':
        return '#FEE2E2';
      case 'warning':
        return '#FEF3C7';
      case 'info':
        return '#DBEAFE';
      default:
        return '#F3F4F6';
    }
  };

  const getSeverityBorderColor = (severity: string) => {
    switch (severity) {
      case 'error':
        return '#EF4444';
      case 'warning':
        return '#F59E0B';
      case 'info':
        return '#3B82F6';
      default:
        return '#D1D5DB';
    }
  };

  const renderIssueGroup = (title: string, issueList: ValidationIssue[], severity: string) => {
    if (issueList.length === 0) return null;

    const isExpanded = expandedSeverity === severity;

    return (
      <div style={{ marginBottom: 12 }}>
        <div
          onClick={() => setExpandedSeverity(isExpanded ? null : severity)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 10,
            background: getSeverityColor(severity),
            border: `1px solid ${getSeverityBorderColor(severity)}`,
            borderRadius: 6,
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isExpanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
            {getSeverityIcon(severity)}
            <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
              {title}
            </span>
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                padding: '2px 8px',
                background: 'white',
                borderRadius: 12,
                color: getSeverityBorderColor(severity),
              }}
            >
              {issueList.length}
            </span>
          </div>
        </div>

        {isExpanded && (
          <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
            {issueList.map((issue) => (
              <div
                key={issue.id}
                onClick={() => onSelectNode?.(issue.nodeId)}
                style={{
                  padding: 12,
                  background: 'white',
                  border: `1px solid ${getSeverityBorderColor(severity)}`,
                  borderLeft: `4px solid ${getSeverityBorderColor(severity)}`,
                  borderRadius: 6,
                  cursor: onSelectNode ? 'pointer' : 'default',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (onSelectNode) {
                    e.currentTarget.style.background = '#F9FAFB';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  {getSeverityIcon(severity)}
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                      {issue.message}
                    </p>
                    {issue.field && (
                      <p style={{ margin: '4px 0 0', fontSize: 11, color: '#9CA3AF' }}>
                        {t('validation.groups.field')} <code style={{ background: '#F3F4F6', padding: '2px 4px', borderRadius: 3 }}>{issue.field}</code>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) {
    return (
      <div
        style={{
          position: 'fixed',
          left: position.x,
          top: position.y,
          background: issues.length === 0 ? '#10B981' : errors.length > 0 ? '#EF4444' : warnings.length > 0 ? '#F59E0B' : '#3B82F6',
          color: 'white',
          padding: 12,
          borderRadius: 8,
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
        onClick={() => setIsOpen(true)}
        onMouseDown={handleMouseDown}
      >
        <div className="drag-handle" style={{ cursor: isDragging ? 'grabbing' : 'grab', display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiMove size={16} />
          {issues.length === 0 ? (
            <>
              <FiCheckCircle size={20} />
              <span style={{ fontSize: 14, fontWeight: 500 }}>{t('validation.minimized.noProblem')}</span>
            </>
          ) : (
            <>
              <FiAlertCircle size={20} />
              <span style={{ fontSize: 14, fontWeight: 500 }}>
                {t('validation.minimized.problems', { count: issues.length })}
              </span>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        width: 380,
        maxHeight: 'calc(100vh - 120px)',
        background: 'white',
        borderRadius: 12,
        boxShadow: isDragging ? '0 12px 32px rgba(0, 0, 0, 0.3)' : '0 8px 24px rgba(0, 0, 0, 0.15)',
        zIndex: isDragging ? 1000 : 10,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: isDragging ? 'none' : 'box-shadow 0.2s ease',
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Header */}
      <div
        className="drag-handle"
        style={{
          padding: 16,
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: issues.length === 0 ? '#F0FDF4' : '#FAFAFA',
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FiMove size={16} style={{ color: '#9CA3AF' }} />
          {issues.length === 0 ? (
            <>
              <FiCheckCircle size={20} style={{ color: '#10B981' }} />
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#10B981' }}>
                  {t('validation.panel.success')}
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6B7280' }}>
                  {t('validation.panel.noProblem')}
                </p>
              </div>
            </>
          ) : (
            <>
              <FiAlertCircle size={20} style={{ color: errors.length > 0 ? '#EF4444' : warnings.length > 0 ? '#F59E0B' : '#3B82F6' }} />
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#111827' }}>
                  {t('validation.panel.title')}
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6B7280' }}>
                  {t('validation.panel.problems', { count: issues.length })}
                </p>
              </div>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              display: 'flex',
              alignItems: 'center',
              borderRadius: 4,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#F3F4F6')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            title={t('validation.panel.minimize')}
          >
            <FiMinus size={18} style={{ color: '#6B7280' }} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              display: 'flex',
              alignItems: 'center',
              borderRadius: 4,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#F3F4F6')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            title={t('validation.panel.close')}
          >
            <FiX size={18} style={{ color: '#6B7280' }} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {issues.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9CA3AF' }}>
            <FiCheckCircle size={48} style={{ margin: '0 auto 16px', color: '#10B981' }} />
            <p style={{ fontSize: 14, margin: 0 }}>
              {t('validation.panel.allCorrect')}
            </p>
          </div>
        ) : (
          <>
            {renderIssueGroup(t('validation.groups.errors'), errors, 'error')}
            {renderIssueGroup(t('validation.groups.warnings'), warnings, 'warning')}
            {renderIssueGroup(t('validation.groups.info'), infos, 'info')}
          </>
        )}
      </div>
    </div>
  );
};

export default ValidationPanel;
